import type { ThunkDependencies } from "single-spa/store/thunk-dependencies";
import type { ThunkDispatchCommon, ThunkGetState } from "single-spa/store/types";

import { showErrorToast } from "app/actions/toast_actions";
import { translate } from "app/filters/i18nFilter";
import { renderErrorToast } from "app/pages/sales-intelligence/common-components/ErrorToast/ErrorToast";
import { getDeleteProfileErrorMessage } from "app/pages/sales-intelligence/modules/employee-profiles/utils/getDeleteProfileErrorMessage";
import { sentryErrorTrackerWithScope } from "app/pages/sales-intelligence/utils/tracking/sentry";

import type { CreateProfilePayload, Profile, UpdateProfilePayload } from "../types/common";

import * as ac from "./action-creators";

/**
 * The API call to fetch customer profiles
 */
export function fetchProfilesThunk() {
    return async function fetchProfiles(dispatch: ThunkDispatchCommon, _: ThunkGetState, deps: ThunkDependencies) {
        dispatch(ac.fetchProfilesAsync.request());

        try {
            const responseDto = await deps.si.api.contactsSearchProfiles.fetchAllProfiles();

            return dispatch(ac.fetchProfilesAsync.success(responseDto));
        } catch (e) {
            return dispatch(ac.fetchProfilesAsync.failure(e?.message));
        }
    };
}
/**
 * The API call to create custom profile
 * @param payload
 */
export function createProfileThunk(payload: CreateProfilePayload) {
    return async function createProfile(dispatch: ThunkDispatchCommon, _: ThunkGetState, deps: ThunkDependencies) {
        dispatch(ac.createProfileAsync.request());

        try {
            const { isEnabled, isExpanded, ...requestDto } = payload;
            const responseDto = await deps.si.api.contactsSearchProfiles.createProfile(requestDto);

            return dispatch(
                ac.createProfileAsync.success({
                    ...responseDto,
                    isEnabled,
                    isExpanded,
                }),
            );
        } catch (e) {
            return dispatch(ac.createProfileAsync.failure(e?.message));
        }
    };
}
/**
 * The API call to update custom profile via given DTO
 * @param payload
 */
export function updateProfileThunk(payload: UpdateProfilePayload) {
    return async function updateProfile(dispatch: ThunkDispatchCommon, _: ThunkGetState, deps: ThunkDependencies) {
        dispatch(ac.updateProfileAsync.request());

        try {
            const { isEnabled, isExpanded, ...requestDto } = payload;
            const responseDto = await deps.si.api.contactsSearchProfiles.updateProfile(requestDto);

            return dispatch(
                ac.updateProfileAsync.success({
                    ...responseDto,
                    isEnabled,
                    isExpanded,
                }),
            );
        } catch (e) {
            return dispatch(ac.updateProfileAsync.failure(e?.message));
        }
    };
}

/**
 * The API call to delete custom profile
 * @param id
 */
export function deleteProfileThunk(id: Profile["id"]) {
    return async function deleteProfile(dispatch: ThunkDispatchCommon, _: ThunkGetState, deps: ThunkDependencies) {
        dispatch(ac.deleteProfileAsync.request());

        try {
            await deps.si.api.contactsSearchProfiles.deleteProfile(id);

            return dispatch(ac.deleteProfileAsync.success(id));
        } catch (e) {
            // The reason lives on the response, so it is read here where the failure is still typed as a
            // request error. Every caller of this thunk gets the toast, so none of them repeat it.
            const serverMessage = await getDeleteProfileErrorMessage(e);

            if (!serverMessage) {
                // A rejected delete is an expected outcome, so only unexplained failures reach Sentry.
                sentryErrorTrackerWithScope({
                    errorMessage: "Failed to delete employee profile",
                    e,
                    tags: { component: "contacts-search-profiles" },
                    params: { extra: { profileId: id } },
                });
            }

            dispatch(
                showErrorToast(
                    renderErrorToast({
                        content: serverMessage ?? translate("si.common.toast.error"),
                        minWidth: "auto",
                    }),
                ),
            );

            return dispatch(ac.deleteProfileAsync.failure(serverMessage ?? e?.message));
        }
    };
}

/**
 * The API call to exclude shared profile
 * @param id
 */
export function excludeProfileThunk(id: Profile["id"]) {
    return async function excludeProfile(dispatch: ThunkDispatchCommon, _: ThunkGetState, deps: ThunkDependencies) {
        dispatch(ac.excludeProfileAsync.request());

        try {
            await deps.si.api.contactsSearchProfiles.excludeProfile(id);

            return dispatch(ac.excludeProfileAsync.success(id));
        } catch (e) {
            return dispatch(ac.excludeProfileAsync.failure(e?.message));
        }
    };
}
